<script setup>
import { computed }  from 'vue'
import Drawer        from 'primevue/drawer'
import Tag           from 'primevue/tag'
import Chip          from 'primevue/chip'
import { useI18n }   from '@/i18n'
import { useFormatDate } from '@/composables/useFormatDate'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  ioc:        { type: Object,  default: null  },
})
const emit = defineEmits(['update:modelValue'])

const { t }       = useI18n()
const { fmtDate } = useFormatDate()

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
})

// ── Type badge ────────────────────────────────────────────────────────────────
const TYPE_SEVERITY = {
  ip: 'danger', 'ip:port': 'danger',
  domain: 'warn', url: 'warn',
  md5: 'info', sha1: 'info', sha256: 'info',
  email: 'secondary', btc: 'secondary',
}
const typeSeverity = (type) => TYPE_SEVERITY[type?.toLowerCase()] ?? 'secondary'

// ── Confidence badge ──────────────────────────────────────────────────────────
const confidenceSeverity = (c) => {
  if (c == null) return 'secondary'
  if (c >= 75)   return 'success'
  if (c >= 40)   return 'warn'
  return 'danger'
}

// ── Threat type badge ─────────────────────────────────────────────────────────
const THREAT_SEVERITY = {
  botnet_cc:        'danger',
  payload_delivery: 'danger',
  cc_skimming:      'warn',
}
const threatSeverity = (tt) => THREAT_SEVERITY[tt?.toLowerCase()] ?? 'secondary'

// ── Source badge ──────────────────────────────────────────────────────────────
const sourceSeverity = (s) =>
  s === 'threatfox' ? 'info' : s === 'ransomware_live' ? 'warn' : 'secondary'

const isThreatFox = computed(() => props.ioc?.source === 'threatfox')
</script>

<template>
  <Drawer
    v-model:visible="visible"
    :header="t('hackwatch.iocs.detailTitle')"
    position="right"
    style="width: 28rem; max-width: 95vw;"
  >
    <div v-if="ioc" class="ioc-detail">

      <!-- ── IOC Value ─────────────────────────────────────────────────────── -->
      <div class="detail-section">
        <div class="detail-label">{{ t('hackwatch.iocs.colValue') }}</div>
        <div class="ioc-value-box">{{ ioc.value }}</div>
      </div>

      <!-- ── Core metadata grid ────────────────────────────────────────────── -->
      <div class="detail-grid">
        <div class="detail-item">
          <div class="detail-label">{{ t('hackwatch.iocs.colType') }}</div>
          <Tag :value="ioc.ioc_type" :severity="typeSeverity(ioc.ioc_type)" />
        </div>

        <div class="detail-item">
          <div class="detail-label">{{ t('hackwatch.iocs.detailSource') }}</div>
          <Tag :value="ioc.source" :severity="sourceSeverity(ioc.source)" />
        </div>

        <div class="detail-item">
          <div class="detail-label">{{ t('hackwatch.iocs.colGroup') }}</div>
          <span class="detail-value">{{ ioc.group_name ?? '—' }}</span>
        </div>

        <div class="detail-item">
          <div class="detail-label">{{ t('hackwatch.iocs.colCreated') }}</div>
          <span class="detail-value">{{ fmtDate(ioc.created_at) }}</span>
        </div>
      </div>

      <!-- ── ThreatFox enrichment ───────────────────────────────────────────── -->
      <template v-if="isThreatFox">
        <div class="detail-divider">
          <span>ThreatFox</span>
        </div>

        <div class="detail-grid">
          <div v-if="ioc.threat_type" class="detail-item">
            <div class="detail-label">{{ t('hackwatch.iocs.colThreatType') }}</div>
            <Tag :value="ioc.threat_type" :severity="threatSeverity(ioc.threat_type)" />
          </div>

          <div v-if="ioc.confidence != null" class="detail-item">
            <div class="detail-label">{{ t('hackwatch.iocs.colConfidence') }}</div>
            <div class="confidence-row">
              <div class="confidence-bar-track">
                <div
                  class="confidence-bar-fill"
                  :class="`conf-${confidenceSeverity(ioc.confidence)}`"
                  :style="{ width: ioc.confidence + '%' }"
                />
              </div>
              <Tag
                :value="ioc.confidence + '%'"
                :severity="confidenceSeverity(ioc.confidence)"
                style="font-size: 0.72rem;"
              />
            </div>
          </div>

          <div v-if="ioc.reporter" class="detail-item">
            <div class="detail-label">{{ t('hackwatch.iocs.colReporter') }}</div>
            <span class="detail-value mono">{{ ioc.reporter }}</span>
          </div>

          <div v-if="ioc.first_seen" class="detail-item">
            <div class="detail-label">{{ t('hackwatch.iocs.colFirstSeen') }}</div>
            <span class="detail-value">{{ fmtDate(ioc.first_seen) }}</span>
          </div>

        </div>

        <div v-if="ioc.tags?.length" class="detail-item full">
          <div class="detail-label">{{ t('hackwatch.iocs.colTags') }}</div>
          <div class="tags-row">
            <Chip v-for="tag in ioc.tags" :key="tag" :label="tag" class="tag-chip" />
          </div>
        </div>
      </template>

      <!-- ── Note ─────────────────────────────────────────────────────────── -->
      <div v-if="ioc.note" class="detail-section">
        <div class="detail-label">{{ t('hackwatch.iocs.colNote') }}</div>
        <div class="note-box">{{ ioc.note }}</div>
      </div>

    </div>
  </Drawer>
</template>

<style scoped>
.ioc-detail {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 0;
}

/* ── Value box ──────────────────────────────────────────────────────────────── */
.ioc-value-box {
  font-family: monospace;
  font-size: 0.9rem;
  word-break: break-all;
  background: var(--p-surface-ground);
  border: 1px solid var(--p-content-border-color);
  border-radius: 6px;
  padding: 8px 12px;
  color: var(--p-text-color);
}

/* ── Metadata grid ──────────────────────────────────────────────────────────── */
.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 16px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.detail-item.full {
  grid-column: 1 / -1;
}

.detail-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--p-text-color-secondary);
}

.detail-value {
  font-size: 0.85rem;
  color: var(--p-text-color);
}
.detail-value.mono {
  font-family: monospace;
}

/* ── Divider ────────────────────────────────────────────────────────────────── */
.detail-divider {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--p-text-color-secondary);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.detail-divider::before,
.detail-divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--p-content-border-color);
}

/* ── Confidence bar ─────────────────────────────────────────────────────────── */
.confidence-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.confidence-bar-track {
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: var(--p-surface-ground);
  overflow: hidden;
  border: 1px solid var(--p-content-border-color);
}
.confidence-bar-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}
.conf-success { background: var(--p-green-500); }
.conf-warn    { background: var(--p-orange-400); }
.conf-danger  { background: var(--p-red-500); }
.conf-secondary { background: var(--p-surface-400); }

/* ── Tags ───────────────────────────────────────────────────────────────────── */
.tags-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
:deep(.tag-chip) {
  font-size: 0.72rem;
  padding: 2px 8px;
  background: var(--p-surface-ground);
  border: 1px solid var(--p-content-border-color);
}

/* ── Note ───────────────────────────────────────────────────────────────────── */
.note-box {
  font-size: 0.82rem;
  color: var(--p-text-color-secondary);
  background: var(--p-surface-ground);
  border: 1px solid var(--p-content-border-color);
  border-radius: 6px;
  padding: 8px 12px;
  white-space: pre-wrap;
}


.detail-section {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
</style>
